import type { TOC } from '@ember/component/template-only';
import type { ComponentLike } from '@glint/template';
import Card from 'tio-ui/components/card';
import Heading from 'tio-ui/components/heading';

interface HeroStatSignature {
  Args: {
    /** What the number counts. Standard tiles show it as an overline; feature tiles show it under the value. */
    label: string;
    /** Displayed as-is. Pass a `:value` block instead to format it, e.g. superscript cents. */
    value?: string | number;
    /** Overline glyph (standard tiles only). */
    icon?: ComponentLike<{ Element: SVGElement }>;
    /** One-line explanation under the value (standard tiles only). */
    hint?: string;
    /** `feature` is the wide headline tile: large value first, then optional `:meter` and `:footer`. */
    emphasis?: 'standard' | 'feature';
  };
  Blocks: {
    /** Replaces `@value`. */
    value?: [];
    /** Progress meter under the value (feature tiles only). */
    meter?: [];
    /** Muted footnote row, laid out space-between (feature tiles only). */
    footer?: [];
  };
  Element: HTMLDivElement;
}

/** One stat tile inset in a `DashboardHero`. */
const HeroStat: TOC<HeroStatSignature> = <template>
  {{#if (eq @emphasis "feature")}}
    <Card @radius="nested" @padding="sm" ...attributes>
      <div class="tio-col">
        <div class="tio-col-flush gap-0.5">
          <div class="tio-text-2xl-bold tio-fg-primary">
            {{#if (has-block "value")}}{{yield to="value"}}{{else}}{{@value}}{{/if}}
          </div>
          <p class="tio-text-xs-normal tio-fg-muted">{{@label}}</p>
        </div>
        {{yield to="meter"}}
        {{#if (has-block "footer")}}
          <div class="tio-row-between tio-text-xs-normal tio-fg-muted">
            {{yield to="footer"}}
          </div>
        {{/if}}
      </div>
    </Card>
  {{else}}
    <Card @radius="nested" @padding="xs" ...attributes>
      <div class="tio-col-flush gap-0.5">
        <div class="tio-text-overline tio-row-snug tio-fg-muted">
          {{#if @icon}}
            {{#let @icon as |Icon|}}
              <Icon class="tio-icon-xs" />
            {{/let}}
          {{/if}}
          {{@label}}
        </div>
        <Heading @level={{3}} @size="lg">
          {{#if (has-block "value")}}{{yield to="value"}}{{else}}{{@value}}{{/if}}
        </Heading>
        {{#if @hint}}
          <span class="tio-text-xs-normal leading-tight tio-fg-muted">{{@hint}}</span>
        {{/if}}
      </div>
    </Card>
  {{/if}}
</template>;

export default HeroStat;
